import { Component, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TableConfig } from '@app/shared/util/table-config.utils';
import { ToolReturnsService } from '@app/core/services/tool-returns.service';
import { ToolReturn } from '@app/shared/models/tool-return.model';
import { ActivatedRoute, Router } from '@angular/router';
import { Log } from 'ng2-logger';
import { Section } from '@models/enums/section.utils';
import { NotificationService } from '@services/notification.service';
import { Subscription } from 'rxjs';
import { AppUtils } from '@app/shared/util/app.utils';
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ResolutionService } from "@services/resolution.service";
import { ToolProcessingCardComponent } from "@components/tool-card/tool-processing-card.component";
import { ToolProcessingEntry } from "@components/tool-card/tool-processing-card.model";
import { ToolRequest } from "@models/toolRequests.model";
import { ToolRequestStatus } from "@app/tool/model/enums/tool-request-status.enum";
import {
    ToolProcessingFilterBottomSheetComponent
} from "@app/roles/kardex-operator/components/tool-processing-bottom-sheet/tool-processing-filter-bottom-sheet.component";

@UntilDestroy()
@Component({
    selector: 'app-incoming-tools-returned',
    templateUrl: './incoming-tools-returned.component.html',
    styleUrls: ['./incoming-tools-returned.component.scss']
})
export class IncomingToolsReturnedComponent implements OnInit, OnDestroy {
    @ViewChild(ToolProcessingFilterBottomSheetComponent, {static: false}) filterSheet: ToolProcessingFilterBottomSheetComponent;

    _logger = Log.create(IncomingToolsReturnedComponent.name);
    tableConfig = new TableConfig();
    isLoading = false;

    tools: Array<ToolReturn> = [];
    // Used on mobile
    toolProcessingEntries: Array<ToolProcessingEntry> = [];
    selectedToolReturn: ToolReturn;

    type: string;
    selectedPlantsChangedSubscription: Subscription;

    @HostBinding('class.mobile') isMobile = false;

    availableStatuses = [
        'All',
        ToolRequestStatus.APPROVED,
        ToolRequestStatus.WAITING_FOR_PROCESSING
    ];
    // TODO left empty until we can clarify if we want to filter by types accordingly
    availableTypes = [];

    constructor(private toolReturnsService: ToolReturnsService,
                private router: Router,
                private route: ActivatedRoute,
                private resolutionService: ResolutionService,
                private notificationService: NotificationService) {
        this.selectedPlantsChangedSubscription = this.notificationService.selectedPlantsChangedAsObservable.subscribe(data => {
            this.getIncomingToolReturns(this.tableConfig);
        });

        this.resolutionService.screenSize$.pipe(untilDestroyed(this)).subscribe(screen => {
            this.isMobile = screen.mobile;
        })
    }

    ngOnInit() {
        this._logger.info("ngOnInit::");
        if (this.route.snapshot.routeConfig.path === 'lines') {
            this.type = 'LINE';
        } else if (this.route.snapshot.routeConfig.path === 'facilities') {
            this.type = 'TICKET';
        }
        this.isLoading = true;
        this.getIncomingToolReturns(this.tableConfig);
    }

    ngOnDestroy(): void {
        if (this.selectedPlantsChangedSubscription) {
            this.selectedPlantsChangedSubscription.unsubscribe();
        }
    }

    getIncomingToolReturns(tableConfig = this.tableConfig) {
        tableConfig = AppUtils.appendPlantsQuery(tableConfig);
        this._logger.info("getIncomingToolReturns:: Retrieving list of incoming tool returns", tableConfig);
        this.toolReturnsService.getAllToolReturns(tableConfig, this.type, Section.ProcessToolReturns).subscribe(
            data => {
                this._logger.info("getIncomingToolReturns:: API returned data", data);
                this.tools = data.items;
                this.toolProcessingEntries = this.computeProcessingEntries(this.tools);
                this.tableConfig.pager.count = data.total;
                this.isLoading = false;
            },
            error => {
                this._logger.error("getIncomingToolReturns:: API returned error", error);
                this.isLoading = false;
            }
        );
    }

    onSortChange(tableConfig: TableConfig) {
        this._logger.info("onSortChange::", this.tableConfig);
        this.isLoading = true;
        this.getIncomingToolReturns(tableConfig);
    }

    onFilter(tableConfig: TableConfig) {
        this._logger.info("onFilter::", this.tableConfig);
        this.isLoading = true;
        this.getIncomingToolReturns(tableConfig);
    }

    onPageChange(tableConfig: TableConfig) {
        this._logger.info("onPageChange::", this.tableConfig);
        this.isLoading = true;
        this.getIncomingToolReturns(tableConfig);
    }

    onProcess(toolReturn: ToolReturn) {
        this._logger.info("onProcess:: Processing tool return", toolReturn);
        this.selectedToolReturn = toolReturn;
        this.toolReturnsService.startProcessingToolReturn(this.selectedToolReturn.id).subscribe(
            data => {
                this._logger.info("onProcess:: API returned data", data);
                this.router.navigate([this.selectedToolReturn.id], {relativeTo: this.route}).then();
            },
            error => {
                this._logger.info("onProcess:: API returned error", error);
            }
        );
    }

    onResumeProcess(toolReturn: ToolReturn) {
        this.selectedToolReturn = toolReturn;
        this.router.navigate([this.selectedToolReturn.id], {relativeTo: this.route});
    }

    onSelect(entry: ToolProcessingEntry) {
        this._logger.info("onSelect:: Processing tool return", entry?.title);
        if ((entry.payload as ToolRequest).status === 'WaitingForProcessing') {
            this.onResumeProcess(entry.payload);
        } else {
            this.onProcess(entry.payload);
        }
    }

    onApplyFilters(config: TableConfig) {
        this.tableConfig = config;
        this.onFilter(this.tableConfig);
        this.filterSheet.close();
    }

    openFilterSheet() {
        this.filterSheet.open();
    }

    private computeProcessingEntries(toolProcessingRequests: Array<ToolReturn>) {
        return toolProcessingRequests.map(returned => ToolProcessingCardComponent.fromToolReturn(returned));
    }

    get title() {
        return this.type && this.type === 'LINE' ?
            'nav.sections.process-tool-returns.items.processLines' :
            'nav.sections.process-tool-returns.items.processFacilities';
    }
}
